import { getApiClient } from '../../client';
import { handleApiError } from '../../utils/handleApiError';
import type {
  AdminArticle,
  AdminArticleListQuery,
  ArticleAnalyticsArticleRow,
  ArticleAnalyticsCtaRow,
  ArticleAnalyticsDetailResponse,
  ArticleAnalyticsOverviewResponse,
  ArticleAnalyticsQuery,
  ArticleAnalyticsReferrerRow,
  ArticleAnalyticsUtmRow,
  ArticleChangeSlugRequest,
  ArticleCreateRequest,
  ArticleDuplicateRequest,
  ArticleImportRequest,
  ArticleJourneyEvent,
  ArticlePreviewTokenRequest,
  ArticlePreviewTokenResponse,
  ArticlePublishRequest,
  ArticleRevertRequest,
  ArticleScheduleRequest,
  ArticleUnpublishRequest,
  ArticleUpdateRequest,
  ArticleValidationResponse,
  DocumentVersion,
  ServiceResponse,
} from '@dopomogai/types';

const ADMIN_ARTICLES_PREFIX = '/admin/articles';

const compactParams = (params: Record<string, any>) =>
  Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== ''),
  );

export const adminArticlesService = {
  list: (filters: AdminArticleListQuery = {}): ServiceResponse<AdminArticle[]> => {
    return getApiClient()
      .get<AdminArticle[]>(ADMIN_ARTICLES_PREFIX, { params: compactParams(filters) })
      .then((res) => res.data)
      .catch((err) => handleApiError(err, 'listing admin articles'));
  },

  create: (payload: ArticleCreateRequest): ServiceResponse<AdminArticle> => {
    return getApiClient()
      .post<AdminArticle>(ADMIN_ARTICLES_PREFIX, payload)
      .then((res) => res.data)
      .catch((err) => handleApiError(err, 'creating article'));
  },

  importArticle: (payload: ArticleImportRequest): ServiceResponse<AdminArticle> => {
    return getApiClient()
      .post<AdminArticle>(`${ADMIN_ARTICLES_PREFIX}/import`, payload)
      .then((res) => res.data)
      .catch((err) => handleApiError(err, 'importing article'));
  },

  get: (articleId: string): ServiceResponse<AdminArticle> => {
    return getApiClient()
      .get<AdminArticle>(`${ADMIN_ARTICLES_PREFIX}/${encodeURIComponent(articleId)}`)
      .then((res) => res.data)
      .catch((err) => handleApiError(err, `fetching article ${articleId}`));
  },

  update: (articleId: string, payload: ArticleUpdateRequest): ServiceResponse<AdminArticle> => {
    return getApiClient()
      .patch<AdminArticle>(`${ADMIN_ARTICLES_PREFIX}/${encodeURIComponent(articleId)}`, payload)
      .then((res) => res.data)
      .catch((err) => handleApiError(err, `updating article ${articleId}`));
  },

  validate: (articleId: string): ServiceResponse<ArticleValidationResponse> => {
    return getApiClient()
      .get<ArticleValidationResponse>(`${ADMIN_ARTICLES_PREFIX}/${encodeURIComponent(articleId)}/validate`)
      .then((res) => res.data)
      .catch((err) => handleApiError(err, `validating article ${articleId}`));
  },

  createPreviewToken: (
    articleId: string,
    payload: ArticlePreviewTokenRequest,
  ): ServiceResponse<ArticlePreviewTokenResponse> => {
    return getApiClient()
      .post<ArticlePreviewTokenResponse>(
        `${ADMIN_ARTICLES_PREFIX}/${encodeURIComponent(articleId)}/preview-token`,
        payload,
      )
      .then((res) => res.data)
      .catch((err) => handleApiError(err, `creating preview token for ${articleId}`));
  },

  publish: (articleId: string, payload: ArticlePublishRequest): ServiceResponse<AdminArticle> => {
    return getApiClient()
      .post<AdminArticle>(`${ADMIN_ARTICLES_PREFIX}/${encodeURIComponent(articleId)}/publish`, payload)
      .then((res) => res.data)
      .catch((err) => handleApiError(err, `publishing article ${articleId}`));
  },

  schedule: (articleId: string, payload: ArticleScheduleRequest): ServiceResponse<AdminArticle> => {
    return getApiClient()
      .post<AdminArticle>(`${ADMIN_ARTICLES_PREFIX}/${encodeURIComponent(articleId)}/schedule`, payload)
      .then((res) => res.data)
      .catch((err) => handleApiError(err, `scheduling article ${articleId}`));
  },

  unpublish: (articleId: string, payload: ArticleUnpublishRequest): ServiceResponse<AdminArticle> => {
    return getApiClient()
      .post<AdminArticle>(`${ADMIN_ARTICLES_PREFIX}/${encodeURIComponent(articleId)}/unpublish`, payload)
      .then((res) => res.data)
      .catch((err) => handleApiError(err, `unpublishing article ${articleId}`));
  },

  duplicate: (articleId: string, payload: ArticleDuplicateRequest): ServiceResponse<AdminArticle> => {
    return getApiClient()
      .post<AdminArticle>(`${ADMIN_ARTICLES_PREFIX}/${encodeURIComponent(articleId)}/duplicate`, payload)
      .then((res) => res.data)
      .catch((err) => handleApiError(err, `duplicating article ${articleId}`));
  },

  changeSlug: (articleId: string, payload: ArticleChangeSlugRequest): ServiceResponse<AdminArticle> => {
    return getApiClient()
      .post<AdminArticle>(`${ADMIN_ARTICLES_PREFIX}/${encodeURIComponent(articleId)}/change-slug`, payload)
      .then((res) => res.data)
      .catch((err) => handleApiError(err, `changing slug for article ${articleId}`));
  },

  listVersions: (articleId: string): ServiceResponse<DocumentVersion[]> => {
    return getApiClient()
      .get<DocumentVersion[]>(`${ADMIN_ARTICLES_PREFIX}/${encodeURIComponent(articleId)}/versions`)
      .then((res) => res.data)
      .catch((err) => handleApiError(err, `listing versions for article ${articleId}`));
  },

  revert: (articleId: string, payload: ArticleRevertRequest): ServiceResponse<AdminArticle> => {
    return getApiClient()
      .post<AdminArticle>(`${ADMIN_ARTICLES_PREFIX}/${encodeURIComponent(articleId)}/revert`, payload)
      .then((res) => res.data)
      .catch((err) => handleApiError(err, `reverting article ${articleId}`));
  },

  analyticsOverview: (
    query: ArticleAnalyticsQuery = {},
  ): ServiceResponse<ArticleAnalyticsOverviewResponse> => {
    return getApiClient()
      .get<ArticleAnalyticsOverviewResponse>(`${ADMIN_ARTICLES_PREFIX}/analytics/overview`, {
        params: compactParams(query),
      })
      .then((res) => res.data)
      .catch((err) => handleApiError(err, 'fetching article analytics overview'));
  },

  analyticsArticles: (
    query: ArticleAnalyticsQuery = {},
  ): ServiceResponse<ArticleAnalyticsArticleRow[]> => {
    return getApiClient()
      .get<ArticleAnalyticsArticleRow[]>(`${ADMIN_ARTICLES_PREFIX}/analytics/articles`, {
        params: compactParams(query),
      })
      .then((res) => res.data)
      .catch((err) => handleApiError(err, 'fetching article analytics table'));
  },

  analyticsDetail: (
    articleId: string,
    query: ArticleAnalyticsQuery = {},
  ): ServiceResponse<ArticleAnalyticsDetailResponse> => {
    return getApiClient()
      .get<ArticleAnalyticsDetailResponse>(`${ADMIN_ARTICLES_PREFIX}/${encodeURIComponent(articleId)}/analytics`, {
        params: compactParams(query),
      })
      .then((res) => res.data)
      .catch((err) => handleApiError(err, `fetching analytics for article ${articleId}`));
  },

  analyticsReferrers: (
    articleId: string,
    query: ArticleAnalyticsQuery = {},
  ): ServiceResponse<ArticleAnalyticsReferrerRow[]> => {
    return getApiClient()
      .get<ArticleAnalyticsReferrerRow[]>(
        `${ADMIN_ARTICLES_PREFIX}/${encodeURIComponent(articleId)}/analytics/referrers`,
        { params: compactParams(query) },
      )
      .then((res) => res.data)
      .catch((err) => handleApiError(err, `fetching referrers for article ${articleId}`));
  },

  analyticsUtm: (
    articleId: string,
    query: ArticleAnalyticsQuery = {},
  ): ServiceResponse<ArticleAnalyticsUtmRow[]> => {
    return getApiClient()
      .get<ArticleAnalyticsUtmRow[]>(`${ADMIN_ARTICLES_PREFIX}/${encodeURIComponent(articleId)}/analytics/utm`, {
        params: compactParams(query),
      })
      .then((res) => res.data)
      .catch((err) => handleApiError(err, `fetching utm breakdown for article ${articleId}`));
  },

  analyticsCtas: (
    articleId: string,
    query: ArticleAnalyticsQuery = {},
  ): ServiceResponse<ArticleAnalyticsCtaRow[]> => {
    return getApiClient()
      .get<ArticleAnalyticsCtaRow[]>(`${ADMIN_ARTICLES_PREFIX}/${encodeURIComponent(articleId)}/analytics/ctas`, {
        params: compactParams(query),
      })
      .then((res) => res.data)
      .catch((err) => handleApiError(err, `fetching cta analytics for article ${articleId}`));
  },

  analyticsJourney: (
    articleId: string,
    profileId: string,
    query: ArticleAnalyticsQuery = {},
  ): ServiceResponse<ArticleJourneyEvent[]> => {
    return getApiClient()
      .get<ArticleJourneyEvent[]>(
        `${ADMIN_ARTICLES_PREFIX}/${encodeURIComponent(articleId)}/analytics/journey/${encodeURIComponent(profileId)}`,
        { params: compactParams(query) },
      )
      .then((res) => res.data)
      .catch((err) => handleApiError(err, `fetching article journey for ${articleId}/${profileId}`));
  },
};
